/**
 * Pricing Page Component
 * Shows subscription plans with inline Stripe Embedded Checkout
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { NavBar } from "@/components/NavBar";

import { SEO } from "@/components/SEO";
import { toast } from "sonner";
import { Check, Loader2, Building, Camera, ArrowLeft, Zap, Tag, X, Bell, MapPin } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function PlanGrid({ user, token, isYearly, hasActiveSub, isOnStudio, isOnSecond, isOnCombined, subscription, studioPlan, secondPlan, combinedPlan, perJobPlan, freePlan, localJobsCheck, notifySignedUp, setNotifySignedUp, onSelectPlan, onCancel, fetchSubscriptionStatus }) {
  const [showAllAccessWarning, setShowAllAccessWarning] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState(null);

  // Determine user's roles
  var roles = user ? (user.roles || []) : [];
  var hasStudioRole = roles.includes("studio");
  var hasAssistantRole = roles.includes("assistant");
  var hasBothRoles = hasStudioRole && hasAssistantRole;

  // Determine recommended plan
  var recommended = null;
  if (user) {
    if (hasBothRoles) recommended = "combined";
    else if (hasStudioRole) recommended = "studio";
    else if (hasAssistantRole) recommended = "second";
  }

  function handlePlanClick(planId) {
    // Check if single-role user is picking All Access
    if (planId.startsWith("combined") && user && !hasBothRoles) {
      setPendingPlanId(planId);
      setShowAllAccessWarning(true);
      return;
    }
    onSelectPlan(planId);
  }

  function confirmAllAccess() {
    setShowAllAccessWarning(false);
    if (pendingPlanId) onSelectPlan(pendingPlanId);
    setPendingPlanId(null);
  }

  function switchToRecommended() {
    setShowAllAccessWarning(false);
    setPendingPlanId(null);
    if (hasStudioRole) onSelectPlan(studioPlan.id);
    else onSelectPlan(secondPlan.id);
  }

  // Build ordered plan cards: recommended first
  var plans = [
    { key: "studio", plan: studioPlan, type: "studio", icon: "building", label: "For Studios", isActive: isOnStudio },
    { key: "second", plan: secondPlan, type: "second", icon: "camera", label: "For Second Shooters", isActive: isOnSecond },
    { key: "combined", plan: combinedPlan, type: "combined", icon: "zap", label: "For Both", isActive: isOnCombined },
  ];

  // Sort: recommended first
  if (recommended) {
    plans.sort(function(a, b) {
      if (a.key === recommended) return -1;
      if (b.key === recommended) return 1;
      return 0;
    });
  }

  return (
    <div className="mb-12" data-testid="plan-grid">
      {/* 3-Column Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {plans.map(function(item) {
          var isRecommended = recommended === item.key;
          var isCurrent = hasActiveSub && item.isActive;
          var isCancelled = isCurrent && subscription?.cancel_at_period_end;

          return (
            <div key={item.key} className="flex flex-col">
              <Card
                className={
                  "flex-1 p-6 relative overflow-hidden transition-all " +
                  (isRecommended
                    ? "bg-gradient-to-br from-coral/10 to-purple-500/5 border-coral/40 ring-1 ring-coral/30"
                    : "bg-white/[0.03] border-white/10") +
                  (isCurrent ? " ring-2 ring-coral" : "")
                }
                data-testid={"plan-card-" + item.key}
              >
                {/* Badges */}
                <div className="flex items-center gap-2 mb-3 min-h-[24px]">
                  {isRecommended && (
                    <Badge className="bg-coral/20 text-coral border-coral/30 text-xs" data-testid={"recommended-" + item.key}>
                      Recommended for you
                    </Badge>
                  )}
                  {isCurrent && (
                    <Badge className="bg-coral text-white border-0 text-xs">Current Plan</Badge>
                  )}
                </div>

                {/* Plan icon + label */}
                <div className="flex items-center gap-2 mb-1 text-white/40">
                  {item.icon === "building" && <Building className="w-4 h-4" />}
                  {item.icon === "camera" && <Camera className="w-4 h-4" />}
                  {item.icon === "zap" && <Zap className="w-4 h-4" />}
                  <span className="text-xs uppercase tracking-widest font-mono">{item.label}</span>
                </div>

                <h3 className="text-xl font-bold mb-1">{item.plan.name}</h3>
                <p className="text-white/50 text-sm mb-4">{item.plan.description}</p>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-bold">${item.plan.price}</span>
                  <span className="text-white/50 text-sm">/{isYearly ? "yr" : "mo"}</span>
                </div>
                {isYearly && item.plan.originalPrice && (
                  <p className="text-sm text-white/40 line-through -mt-3 mb-4">${item.plan.originalPrice}/{isYearly ? "yr" : "mo"}</p>
                )}

                {/* Features */}
                <ul className="space-y-2 mb-6 flex-1">
                  {item.plan.features.map(function(f, i) {
                    return (
                      <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                        <Check className="w-4 h-4 text-coral flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    );
                  })}
                </ul>

                {/* CTA Button */}
                <Button
                  onClick={function() { handlePlanClick(item.plan.id); }}
                  disabled={isCurrent && !isCancelled}
                  className={
                    "w-full font-bold py-3 " +
                    (isRecommended
                      ? "bg-coral hover:bg-coral/90 text-white"
                      : "bg-white text-black hover:bg-gray-200")
                  }
                  data-testid={"select-" + item.plan.id}
                >
                  {isCurrent ? "Current Plan" : user ? "Get " + item.plan.name : "Sign Up"}
                </Button>

                {/* Cancel button */}
                {isCurrent && !isCancelled && (
                  <Button
                    onClick={onCancel}
                    variant="outline"
                    className="w-full mt-2 border-white/20 text-white/50 hover:bg-white/10 hover:text-white text-xs"
                    data-testid={"cancel-" + item.key + "-subscription"}
                  >
                    Cancel Subscription
                  </Button>
                )}
                {isCancelled && (
                  <div className="mt-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-sm text-center">
                    <p className="text-yellow-400 text-sm font-medium">Cancelled</p>
                    <p className="text-white/40 text-xs mt-1">Access until {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : "end of period"}</p>
                  </div>
                )}

                {/* No local jobs alert — show on second shooter plan */}
                {item.key === "second" && user && localJobsCheck && !hasActiveSub && localJobsCheck.open_jobs_nearby === 0 && (
                  <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-sm" data-testid="no-local-jobs-alert">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs text-yellow-300 font-medium mb-1">No open jobs near {localJobsCheck.city || "you"} right now</p>
                        <p className="text-xs text-white/40 mb-2">Studios can still discover your free profile.</p>
                        {!notifySignedUp ? (
                          <button
                            onClick={async function() {
                              try {
                                var res = await fetch(API + "/subscriptions/notify-jobs", { method: "POST", headers: { Authorization: "Bearer " + token } });
                                if (res.ok) { setNotifySignedUp(true); toast.success("You'll be notified when jobs open nearby!"); }
                              } catch (e) { /* ignore */ }
                            }}
                            className="inline-flex items-center gap-1.5 text-xs text-yellow-400 hover:text-yellow-300 font-medium"
                            data-testid="notify-jobs-btn"
                          >
                            <Bell className="w-3 h-3" /> Notify me when jobs open nearby
                          </button>
                        ) : (
                          <p className="text-xs text-green-400 flex items-center gap-1">
                            <Check className="w-3 h-3" /> You'll be notified
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Jobs available badge */}
                {item.key === "second" && user && localJobsCheck && localJobsCheck.open_jobs_nearby > 0 && !hasActiveSub && (
                  <div className="mt-4 p-2 bg-green-500/10 border border-green-500/20 rounded-sm" data-testid="local-jobs-available">
                    <p className="text-xs text-green-400 flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" />
                      {localJobsCheck.open_jobs_nearby} open job{localJobsCheck.open_jobs_nearby !== 1 ? "s" : ""} near {localJobsCheck.city || "you"}
                    </p>
                  </div>
                )}
              </Card>

              {/* Sub-cards below each plan */}
              {item.key === "studio" && (
                <Card className="bg-white/[0.02] border-white/5 p-4 mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-sm">{perJobPlan.name}</h4>
                    <span className="font-bold">${perJobPlan.price}<span className="text-white/40 text-xs font-normal">/job</span></span>
                  </div>
                  <p className="text-white/40 text-xs">{perJobPlan.description}</p>
                </Card>
              )}
              {item.key === "second" && (
                <Card className="bg-white/[0.02] border-white/5 p-4 mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-sm">{freePlan.name}</h4>
                    <span className="font-bold text-coral">Free</span>
                  </div>
                  <p className="text-white/40 text-xs">{freePlan.description}</p>
                </Card>
              )}
              {item.key === "combined" && (
                <Card className="bg-white/[0.02] border-white/5 p-4 mt-3">
                  <p className="text-white/40 text-xs text-center">For photographers who both hire second shooters and work as second shooters</p>
                </Card>
              )}
            </div>
          );
        })}
      </div>

      {/* All Access Warning Dialog for single-role users */}
      <Dialog open={showAllAccessWarning} onOpenChange={setShowAllAccessWarning}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Are you sure you need All Access?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-white/60 text-sm leading-relaxed">
              {hasStudioRole
                ? "You're registered as a studio. The All Access plan ($" + combinedPlan.price + "/mo) includes second shooter features you may not need. Studio Pro ($" + studioPlan.price + "/mo) has everything you need to hire talent."
                : "You're registered as a second shooter. The All Access plan ($" + combinedPlan.price + "/mo) includes studio features you may not need. Second Shooter Pro ($" + secondPlan.price + "/mo) gives you full job access at a lower price."
              }
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={switchToRecommended}
                className="w-full bg-coral hover:bg-coral/90 text-white font-bold"
                data-testid="switch-to-recommended"
              >
                {hasStudioRole
                  ? "Get Studio Pro ($" + studioPlan.price + "/mo) — Recommended"
                  : "Get Second Shooter Pro ($" + secondPlan.price + "/mo) — Recommended"
                }
              </Button>
              <Button
                onClick={confirmAllAccess}
                variant="outline"
                className="w-full border-white/20 text-white/60 hover:bg-white/10"
                data-testid="confirm-all-access"
              >
                Continue with All Access (${combinedPlan.price}/mo)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [isYearly, setIsYearly] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [validatedDiscount, setValidatedDiscount] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [localJobsCheck, setLocalJobsCheck] = useState(null);
  const [notifySignedUp, setNotifySignedUp] = useState(false);

  const jobId = searchParams.get("job_id"); // For per-job payment

  // Set page title (Stripe SDK loading can delay react-helmet-async)
  useEffect(() => {
    document.title = "Pricing | GottaSecond";
  }, []);

  // Fetch current subscription status
  useEffect(() => {
    if (token) {
      fetchSubscriptionStatus();
      // Check local jobs for second shooters
      (async () => {
        try {
          const res = await fetch(`${API}/subscriptions/local-jobs-check`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) setLocalJobsCheck(await res.json());
        } catch {}
      })();
    }
  }, [token]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${API}/subscriptions/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    }
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) return;
    setValidatingCode(true);
    try {
      const response = await fetch(`${API}/admin/discount-codes/validate?code=${encodeURIComponent(discountCode)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.valid) {
        setValidatedDiscount(data);
        toast.success(`Discount code "${data.code}" applied!`);
      } else {
        setValidatedDiscount(null);
        toast.error(data.error || "Invalid discount code");
      }
    } catch (error) {
      toast.error("Failed to validate code");
    } finally {
      setValidatingCode(false);
    }
  };

  const fetchClientSecret = useCallback(async () => {
    const params = new URLSearchParams({
      plan_type: checkoutPlan,
      origin_url: window.location.origin,
      embedded: "true"
    });
    if (jobId) params.append("job_id", jobId);
    if (validatedDiscount?.code) params.append("discount_code", validatedDiscount.code);

    const response = await fetch(`${API}/subscriptions/checkout?${params.toString()}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to start checkout");
    }

    const data = await response.json();
    return data.client_secret;
  }, [checkoutPlan, jobId, validatedDiscount, token]);

  const handleSelectPlan = (planType) => {
    if (!user) {
      navigate(`/register?plan=${planType}`);
      return;
    }
    // Show confirmation step with discount code option before checkout
    setCheckoutPlan(planType);
    setShowPreCheckout(true);
  };

  const confirmCheckout = () => {
    setShowPreCheckout(false);
    setShowCheckout(true);
  };

  const [showPreCheckout, setShowPreCheckout] = useState(false);

  const closeCheckout = () => {
    setShowCheckout(false);
    setCheckoutPlan(null);
  };

  // Plans configuration
  const studioPlans = {
    monthly: {
      id: "studio_monthly",
      name: "Studio Pro",
      price: 19,
      period: "month",
      description: "Unlimited access to find second shooters",
      features: [
        "View all candidates in your area",
        "Unlimited job postings",
        "Direct messaging with candidates",
        "Contract templates & e-signatures",
        "Save favorites",
        "Match scoring to find perfect fits"
      ],
      popular: true
    },
    yearly: {
      id: "studio_yearly",
      name: "Studio Pro",
      price: 190,
      originalPrice: 228,
      period: "year",
      description: "Save 2 months with yearly billing",
      features: [
        "View all candidates in your area",
        "Unlimited job postings",
        "Direct messaging with candidates",
        "Contract templates & e-signatures",
        "Save favorites",
        "Match scoring to find perfect fits",
        "2 months free"
      ],
      popular: true
    }
  };

  const secondPlans = {
    monthly: {
      id: "second_monthly",
      name: "Second Shooter Pro",
      price: 9,
      period: "month",
      description: "Browse jobs and connect with studios",
      features: [
        "Browse all job listings",
        "Message studios with open jobs",
        "Review & sign contracts digitally",
        "Email notifications for new jobs",
        "Priority in search results"
      ],
      popular: true
    },
    yearly: {
      id: "second_yearly",
      name: "Second Shooter Pro",
      price: 90,
      originalPrice: 108,
      period: "year",
      description: "Save 2 months with yearly billing",
      features: [
        "Browse all job listings",
        "Message studios with open jobs",
        "Review & sign contracts digitally",
        "Email notifications for new jobs",
        "Priority in search results",
        "2 months free"
      ],
      popular: true
    }
  };

  const combinedPlans = {
    monthly: {
      id: "combined_monthly",
      name: "All Access",
      price: 25,
      period: "month",
      description: "Full access as studio and second",
      features: [
        "Everything in Studio Pro",
        "Everything in Second Shooter Pro",
        "Contract templates & e-signatures",
        "Post jobs and browse jobs",
        "Best for multi-role photographers"
      ],
      popular: false,
      highlight: true
    },
    yearly: {
      id: "combined_yearly",
      name: "All Access",
      price: 250,
      originalPrice: 300,
      period: "year",
      description: "Best value - save 2 months",
      features: [
        "Everything in Studio Pro",
        "Everything in Second Shooter Pro",
        "Contract templates & e-signatures",
        "Post jobs and browse jobs",
        "Best for multi-role photographers",
        "2 months free"
      ],
      popular: false,
      highlight: true
    }
  };

  const perJobPlan = {
    id: "studio_per_job",
    name: "Per Job",
    price: 10,
    period: "job",
    description: "Pay only when you need help",
    features: [
      "View all candidates for this job",
      "Direct messaging with candidates",
      "Save favorites",
      "Match scoring to find perfect fits"
    ],
    popular: false
  };

  const freePlan = {
    id: "free",
    name: "Basic (Free Forever)",
    price: 0,
    period: "forever",
    description: "Always free to register",
    features: [
      "Create your full profile",
      "Be listed as eligible for jobs in your area",
      "Get discovered and booked by pros",
      "Receive messages from studios",
      "Accept job offers and get hired"
    ],
    popular: false
  };

  const currentStudioPlan = isYearly ? studioPlans.yearly : studioPlans.monthly;
  const currentSecondPlan = isYearly ? secondPlans.yearly : secondPlans.monthly;
  const currentCombinedPlan = isYearly ? combinedPlans.yearly : combinedPlans.monthly;

  // Determine which plan the user is currently on
  const userPlanType = subscription?.plan_type || "";
  const isOnStudio = userPlanType.startsWith("studio") && !userPlanType.includes("per_job");
  const isOnSecond = userPlanType.startsWith("second");
  const isOnCombined = userPlanType.startsWith("combined");
  const isOnPerJob = userPlanType.includes("per_job");
  const hasActiveSub = subscription?.has_subscription;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO
        title="Pricing"
        description="GottaSecond pricing plans for studios. Get unlimited access to vetted second shooters, photography assistants, and videography professionals. Free for freelancers."
        path="/pricing"
      />
      {/* Navigation */}
      <NavBar>
        {user ? (
          <Link to="/dashboard">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
              Dashboard
            </Button>
          </Link>
        ) : (
          <Link to="/login">
            <Button className="bg-white text-black hover:bg-gray-200">
              Sign In
            </Button>
          </Link>
        )}
      </NavBar>

      <main className="pt-24 pb-16 px-4 md:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Back button */}
          <Link 
            to={user ? "/dashboard" : "/"} 
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{user ? "Back to Dashboard" : "Back to Home"}</span>
          </Link>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-white/60 text-lg mb-4">
              Choose the plan that works for you
            </p>
            
            {/* Always Free Banner */}
            <div className="inline-flex items-center gap-2 bg-coral/10 border border-coral/30 rounded-full px-6 py-2 mb-8">
              <Check className="w-5 h-5 text-coral" />
              <span className="text-coral font-medium">Always FREE to register</span>
              <span className="text-white/60">— Studios and Second Shooters can create an account and join the talent pool at no cost</span>
            </div>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-1 mb-8" data-testid="billing-toggle">
              <button
                onClick={() => setIsYearly(false)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${!isYearly ? "bg-white text-black" : "bg-white/10 text-white/60 hover:bg-white/15 hover:text-white/80"}`}
                data-testid="billing-toggle-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${isYearly ? "bg-white text-black" : "bg-white/10 text-white/60 hover:bg-white/15 hover:text-white/80"}`}
                data-testid="billing-toggle-yearly"
              >
                Yearly
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${isYearly ? "bg-coral text-white" : "bg-coral/20 text-coral"}`}>Save 2 months</span>
              </button>
            </div>

            {/* Discount Code Input */}
            {user && (
              <div className="max-w-md mx-auto mb-8">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <Input
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      onKeyPress={(e) => e.key === "Enter" && validateDiscountCode()}
                      placeholder="Have a discount code?"
                      className="bg-white/[0.05] border-white/10 text-white pl-10 h-10 rounded-sm uppercase font-mono"
                      data-testid="discount-code-input"
                    />
                  </div>
                  <Button
                    onClick={validateDiscountCode}
                    disabled={validatingCode || !discountCode.trim()}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 h-10"
                    data-testid="apply-discount-btn"
                  >
                    {validatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                  </Button>
                </div>
                {validatedDiscount && (
                  <div className="mt-2 p-3 bg-green-500/10 border border-green-500/30 rounded-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <span className="text-green-400 font-medium">{validatedDiscount.code}</span>
                      </div>
                      <span className="text-green-400 text-sm">
                        {validatedDiscount.discount_type === "free" && "100% Off"}
                        {validatedDiscount.discount_type === "percentage" && `${validatedDiscount.discount_amount}% Off`}
                        {validatedDiscount.discount_type === "fixed_dollar" && `$${validatedDiscount.discount_amount} Off`}
                        {validatedDiscount.duration_months === 0 && " • Forever"}
                        {validatedDiscount.duration_months === 1 && " • First month"}
                        {validatedDiscount.duration_months > 1 && ` • ${validatedDiscount.duration_months} months`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Current subscription status - Prominent display */}
          {subscription?.has_subscription && (
            <Card className="bg-gradient-to-r from-coral/20 to-coral/5 border-coral/40 p-6 mb-8 max-w-2xl mx-auto">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-coral/20 flex items-center justify-center">
                    <Check className="w-6 h-6 text-coral" />
                  </div>
                  <div>
                    <p className="text-sm text-white/60 uppercase tracking-wider">Your Current Plan</p>
                    <p className="text-xl font-bold text-white capitalize">{subscription.plan_type?.replace("_", " ").replace("combined", "All Access")}</p>
                  </div>
                </div>
                {subscription.current_period_end && (
                  <div className="text-right">
                    <p className="text-sm text-white/50">Renews</p>
                    <p className="text-white font-medium">{new Date(subscription.current_period_end).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* All Plans — 3 Column Grid, Role-Aware Ordering */}
          <PlanGrid
            user={user}
            token={token}
            isYearly={isYearly}
            hasActiveSub={hasActiveSub}
            isOnStudio={isOnStudio}
            isOnSecond={isOnSecond}
            isOnCombined={isOnCombined}
            subscription={subscription}
            studioPlan={currentStudioPlan}
            secondPlan={currentSecondPlan}
            combinedPlan={currentCombinedPlan}
            perJobPlan={perJobPlan}
            freePlan={freePlan}
            localJobsCheck={localJobsCheck}
            notifySignedUp={notifySignedUp}
            setNotifySignedUp={setNotifySignedUp}
            onSelectPlan={handleSelectPlan}
            onCancel={async () => {
              if (!confirm("Cancel your subscription? You'll keep access until the end of your billing period. Your free registration stays active.")) return;
              try {
                const res = await fetch(API + "/subscriptions/cancel", { method: "POST", headers: { Authorization: "Bearer " + token } });
                const data = await res.json();
                if (res.ok) { toast.success(data.message || "Subscription cancelled"); fetchSubscriptionStatus(); }
                else { toast.error(data.detail || "Failed to cancel"); }
              } catch (e) { toast.error("Network error"); }
            }}
            fetchSubscriptionStatus={fetchSubscriptionStatus}
          />

          {/* FAQ or additional info */}
          <div className="text-center text-white/40 text-sm">
            <p>All plans can be cancelled anytime. Questions? <Link to="/contact" className="text-coral hover:underline">Contact us</Link></p>
          </div>
        </div>
      </main>

      {/* Pre-Checkout: Discount Code + Confirm */}
      {showPreCheckout && checkoutPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 bg-[#1a1a1a] border-white/10 p-6">
            <h3 className="text-lg font-bold mb-1">Confirm Your Plan</h3>
            <p className="text-white/50 text-sm mb-5">
              {checkoutPlan.replace(/_/g, " ").replace(/\b\w/g, function(c) { return c.toUpperCase(); }).replace("Combined", "All Access")}
            </p>

            {/* Discount Code */}
            <div className="mb-5">
              <label className="text-white/50 text-sm mb-2 block">Have a discount code?</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === "Enter" && validateDiscountCode()}
                    placeholder="Enter code"
                    className="bg-white/[0.05] border-white/10 text-white pl-10 h-10 rounded-sm uppercase font-mono"
                    data-testid="pre-checkout-discount-input"
                  />
                </div>
                <Button
                  onClick={validateDiscountCode}
                  disabled={validatingCode || !discountCode.trim()}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 h-10"
                  data-testid="pre-checkout-apply-btn"
                >
                  {validatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                </Button>
              </div>
              {validatedDiscount && (
                <div className="mt-2 p-3 bg-green-500/10 border border-green-500/30 rounded-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 font-medium">{validatedDiscount.code}</span>
                    <span className="text-green-400 text-sm ml-auto">
                      {validatedDiscount.discount_type === "free" && "100% Off"}
                      {validatedDiscount.discount_type === "percentage" && `${validatedDiscount.discount_amount}% Off`}
                      {validatedDiscount.discount_type === "fixed_dollar" && `$${validatedDiscount.discount_amount} Off`}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={confirmCheckout}
                className="flex-1 bg-coral hover:bg-coral/90 text-white font-bold"
                data-testid="confirm-checkout-btn"
              >
                Continue to Payment
              </Button>
              <Button
                onClick={() => { setShowPreCheckout(false); setCheckoutPlan(null); }}
                variant="outline"
                className="border-white/20 text-white/60 hover:bg-white/10"
                data-testid="cancel-pre-checkout-btn"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Embedded Checkout Modal */}
      {showCheckout && checkoutPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="relative w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto rounded-lg bg-white">
            <button
              onClick={closeCheckout}
              className="absolute top-3 right-3 z-10 p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              data-testid="close-checkout-btn"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <div className="p-1">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{ fetchClientSecret }}
              >
                <EmbeddedCheckout data-testid="stripe-embedded-checkout" />
              </EmbeddedCheckoutProvider>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
